<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import { showModal } from '@/Utils/modalBus'
import SiteAdminService, { isPaywallSiteUnconfigured } from '@/services/SiteAdminService'
import { trackSites, SITES_EVENTS } from '@/lib/tracking/sites'
import { formatPrice } from '@/Utils/siteAccess'
import { formatMoney, TX_STATUS_LABELS, TX_STATUS_VARIANTS } from '@/Utils/siteTransactions'
import {
	PROMO_TYPES, PROMO_TYPE_LABELS, PROMO_REASON_TEXT, normalizeCode, codeError, valueError,
	promoValueLabel, promoStatus, redemptionsLabel, promoPlansLabel, plansCurrency,
} from '@/Utils/sitePromos'
import { fromLocalInput, fmtDateTime } from '@/Utils/siteSchedule'
import Spinner from '@/components/ui/Spinner.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import ModalBox from '@/components/modals/ModalBox.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import FormCheckbox from '@/components/Atoms/FormCheckbox.vue'
import FormPagination from '@/components/Atoms/FormPagination.vue'
import StatusPill from '@/components/Atoms/StatusPill.vue'
import TabGroup from '@/components/Atoms/TabGroup.vue'
import TabItem from '@/components/Atoms/TabItem.vue'
import Alert from '@/components/Atoms/Alert.vue'
import ButtonCopyToClipboard from '@/components/Moleculas/ButtonCopyToClipboard.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconPlus from '@/components/icon/IconPlus.vue'
import IconBin from '@/components/icon/IconBin.vue'

// Promo codes for the site's plans (castr §6f). One code = one group of
// paywall promos, one per plan it covers, so "max redemptions" counts per
// plan. Viewers type the code at checkout on the site.
const props = defineProps({
	siteId: {
		type: String,
		required: true,
	},
	site: {
		type: Object,
		required: true,
	},
	/** Sellable plans of the site (`useSiteAdmin().sellablePlans`). */
	plans: {
		type: Array,
		default: () => [],
	},
})
const emit = defineEmits(['create-plan'])
const { notify } = useNotification()

const PER_PAGE = 50
const loading = ref(true)
const loadError = ref(null)
const rows = ref([])
const meta = ref({ total: 0, last_page: 1 })
const page = ref(1)
const busyId = ref(null)

const paywallUnlinked = computed(() => !props.site.billing?.paywallSiteId || isPaywallSiteUnconfigured(loadError.value))
const describeError = (err, fallback) => PROMO_REASON_TEXT[err?.reason] || err?.message || fallback
const activePlans = computed(() => props.plans.filter((p) => p.active !== false))
const planById = (id) => props.plans.find((p) => p.public_id === id)

const load = async () => {
	loading.value = true
	try {
		const res = await SiteAdminService.listPromos(props.siteId, { page: page.value, per_page: PER_PAGE })
		rows.value = res?.data || []
		meta.value = res?.meta || { total: rows.value.length, last_page: 1 }
		loadError.value = null
	} catch (err) {
		loadError.value = err
		rows.value = []
	}
	loading.value = false
}
watch(page, load)
onMounted(load)

const replaceRow = (promo) => { rows.value = rows.value.map((r) => (r.id === promo.id ? promo : r)) }

// --- create --------------------------------------------------------------------
const createOpen = ref(false)
const creating = ref(false)
const createError = ref(null)
const form = ref(blankForm())
function blankForm() {
	return { code: '', type: 'percent', value: '', allPlans: true, productIds: [], maxRedemptions: '', expiresAt: '' }
}
const selectedPlans = computed(() => (form.value.allPlans ? activePlans.value : form.value.productIds.map(planById).filter(Boolean)))
const recurringSelected = computed(() => selectedPlans.value.filter((p) => p.recurring))
// The paywall only supports comp codes on subscriptions (castr answers 422
// not_allowed_on_subscriptions otherwise) — switch for the owner and say why.
const typeSwitched = ref(false)
const enforceCompOnSubscriptions = () => {
	if (recurringSelected.value.length && form.value.type !== 'comp') {
		form.value.type = 'comp'
		typeSwitched.value = true
	}
}
// The watcher only fires when the selection really changes; a fresh form with
// "All plans" on yields the same array, so `openCreate` applies the rule itself.
watch(recurringSelected, enforceCompOnSubscriptions)
const typeDisabled = (type) => type !== 'comp' && recurringSelected.value.length > 0
const codeErr = computed(() => codeError(form.value.code))
const valueErr = computed(() => valueError(form.value.type, form.value.value))
const maxErr = computed(() => {
	const s = String(form.value.maxRedemptions).trim()
	if (!s) return ''
	const n = Number(s)
	return Number.isInteger(n) && n >= 1 ? '' : 'Whole number of at least 1, or leave empty for unlimited.'
})
const expiresIso = computed(() => fromLocalInput(form.value.expiresAt))
const expiresErr = computed(() => {
	if (!form.value.expiresAt.trim()) return ''
	if (!expiresIso.value) return 'Enter a valid date and time.'
	return new Date(expiresIso.value).getTime() > Date.now() ? '' : 'The expiry must be in the future.'
})
const currencyLabel = computed(() => plansCurrency(selectedPlans.value) || 'plan currency')
const canCreate = computed(() => normalizeCode(form.value.code).length > 0 && !codeErr.value && !valueErr.value && !maxErr.value && !expiresErr.value
	&& selectedPlans.value.length > 0 && !creating.value)
const previewLabel = computed(() => {
	const code = normalizeCode(form.value.code) || 'CODE'
	if (form.value.type === 'comp') return `${code} makes the selected plans free.`
	if (form.value.type === 'percent') return valueErr.value ? '' : `${code} takes ${Number(form.value.value)} % off the selected plans.`
	return valueErr.value ? '' : `${code} takes ${formatMoney(form.value.value, plansCurrency(selectedPlans.value) || undefined)} off the selected plans.`
})

const openCreate = () => {
	form.value = blankForm()
	typeSwitched.value = false
	createError.value = null
	enforceCompOnSubscriptions()
	createOpen.value = true
}
const onCodeInput = (v) => { form.value.code = normalizeCode(v).slice(0, 32) }
const togglePlan = (id, on) => {
	const set = new Set(form.value.productIds)
	if (on) set.add(id)
	else set.delete(id)
	form.value.productIds = [...set]
}
const submitCreate = async () => {
	if (!canCreate.value) return
	creating.value = true
	createError.value = null
	const body = {
		code: normalizeCode(form.value.code),
		type: form.value.type,
		products: form.value.allPlans ? 'all' : [...form.value.productIds],
	}
	if (form.value.type !== 'comp') body.value = Number(form.value.value)
	if (String(form.value.maxRedemptions).trim()) body.max_redemptions = Number(form.value.maxRedemptions)
	if (expiresIso.value) body.expires_at = expiresIso.value
	try {
		const promo = await SiteAdminService.createPromo(props.siteId, body)
		trackSites(SITES_EVENTS.PROMO_CREATED, props.siteId, { discount_type: body.type, all_plans: body.products === 'all' })
		rows.value = [promo, ...rows.value]
		meta.value = { ...meta.value, total: (meta.value.total || 0) + 1 }
		notify({ group: 'success', text: `Code ${promo.code} created for ${promo.products?.length || 0} ${promo.products?.length === 1 ? 'plan' : 'plans'}.` })
		createOpen.value = false
	} catch (err) {
		const offending = Array.isArray(err?.payload?.products) ? err.payload.products : []
		const names = offending.map((id) => planById(id)?.title || id)
		createError.value = describeError(err, 'Could not create the code.') + (names.length ? ` (${names.join(', ')})` : '')
	}
	creating.value = false
}

// --- activate / deactivate / delete -------------------------------------------
const setActive = async (promo, active) => {
	busyId.value = promo.id
	try {
		const updated = await SiteAdminService.updatePromo(props.siteId, promo.id, { active })
		replaceRow(updated?.id ? updated : { ...promo, active })
		notify({ group: 'success', text: active ? `${promo.code} is active again.` : `${promo.code} deactivated — viewers can no longer redeem it.` })
	} catch (err) {
		notify({ group: 'error', text: describeError(err, 'Could not update the code.') })
	}
	busyId.value = null
}
const pendingDelete = ref(null)
const requestDelete = (promo) => { pendingDelete.value = promo; showModal('delete-site-promo') }
const confirmDelete = async () => {
	if (!pendingDelete.value) return
	const promo = pendingDelete.value
	try {
		await SiteAdminService.deletePromo(props.siteId, promo.id)
		rows.value = rows.value.filter((r) => r.id !== promo.id)
		meta.value = { ...meta.value, total: Math.max(0, (meta.value.total || 1) - 1) }
		notify({ group: 'success', text: `Code ${promo.code} deleted.` })
	} catch (err) {
		notify({ group: 'error', text: describeError(err, 'Could not delete the code.') })
	}
	pendingDelete.value = null
}

// --- redemptions drawer --------------------------------------------------------
const drawerOpen = ref(false)
const drawerPromo = ref(null)
const redemptions = ref([])
const redemptionsMeta = ref({ total: 0 })
const redemptionsPage = ref(1)
const redemptionsLoading = ref(false)
const redemptionsError = ref(null)
const loadRedemptions = async () => {
	if (!drawerPromo.value) return
	redemptionsLoading.value = true
	redemptionsError.value = null
	try {
		const res = await SiteAdminService.listPromoRedemptions(props.siteId, drawerPromo.value.id, { page: redemptionsPage.value, per_page: PER_PAGE })
		redemptions.value = res?.data || []
		redemptionsMeta.value = res?.meta || { total: redemptions.value.length }
	} catch (err) {
		redemptionsError.value = err
		redemptions.value = []
	}
	redemptionsLoading.value = false
}
watch(redemptionsPage, loadRedemptions)
const openRedemptions = (promo) => {
	drawerPromo.value = promo
	redemptions.value = []
	redemptionsPage.value = 1
	drawerOpen.value = true
	loadRedemptions()
}

const fmtExpiry = (promo) => (promo.expires_at ? fmtDateTime(promo.expires_at) : 'Never')
const planLine = (p) => `${p.title} · ${formatPrice(p)}${p.recurring ? ' · subscription' : ''}`
</script>

<template>
	<div data-testid="promo-codes">
		<div class="mb-4 flex flex-wrap items-center gap-3">
			<div class="min-w-0 flex-1">
				<h2 class="mb-0.5 text-headline font-semibold text-surface-text-2">Promo codes</h2>
				<p class="mb-0 text-tiny text-surface-7">
					Discounts on your plans. Viewers type the code at checkout on your site and see the reduced price before paying.
				</p>
			</div>
			<FormButton size="md" class="shrink-0" :disabled="paywallUnlinked || !activePlans.length" :title="!activePlans.length ? 'Create a plan first' : ''" @click="openCreate">
				<icon-base class="mr-1.5" :width="14" :height="14"><icon-plus /></icon-base>
				New promo code
			</FormButton>
		</div>

		<Alert v-if="paywallUnlinked" type="normal" class="mb-4" data-testid="promos-unlinked">
			This site is not linked to your Paywall account yet, so promo codes cannot be created. Retry publish from the Overview tab.
		</Alert>
		<Alert v-else-if="loadError" type="error" class="mb-4" data-testid="promos-load-error">
			{{ describeError(loadError, 'Could not load promo codes.') }}
			<span v-if="loadError.reason" class="ml-1 font-mono text-tiny text-surface-8">({{ loadError.reason }})</span>
		</Alert>

		<template v-if="!paywallUnlinked">
			<Spinner v-if="loading" text="Loading promo codes…" classes="text-surface-8 mt-24" spinner-color="var(--c-dark-8)" spinner-size="15px" />

			<EmptyState v-else-if="!rows.length && !loadError" title="No promo codes yet" subtitle="Create a code for one plan, several, or all of them. Viewers enter it at checkout on your site.">
				<template v-if="activePlans.length" #actions>
					<FormButton class="w-64 justify-center" @click="openCreate">New promo code</FormButton>
				</template>
				<template v-else #actions>
					<FormButton class="w-64 justify-center" type="secondary" @click="emit('create-plan')">Create a plan first</FormButton>
				</template>
			</EmptyState>

			<div v-else-if="rows.length" class="overflow-x-auto rounded-lg bg-surface-2 p-2">
				<table class="w-full min-w-[820px] border-separate border-spacing-y-1 text-left" data-testid="promos-table">
					<thead>
						<tr class="text-[11px] uppercase tracking-wider text-surface-6">
							<th class="px-3 pb-1 font-semibold">Code</th>
							<th class="px-3 pb-1 font-semibold">Discount</th>
							<th class="px-3 pb-1 font-semibold">Plans</th>
							<th class="px-3 pb-1 font-semibold">Redemptions</th>
							<th class="px-3 pb-1 font-semibold">Expires</th>
							<th class="px-3 pb-1 font-semibold">Status</th>
							<th class="px-3 pb-1 text-right font-semibold">Actions</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="promo in rows" :key="promo.id" class="group" :data-testid="`promo-${promo.id}`">
							<td class="rounded-l-lg bg-surface-3/60 px-3 py-2.5 align-middle transition-colors group-hover:bg-surface-3">
								<span class="flex items-center gap-1.5">
									<span class="font-mono text-sm font-medium text-white">{{ promo.code }}</span>
									<ButtonCopyToClipboard :text="promo.code" size="sm" fixed-width="w-auto" label="Copy code" />
								</span>
							</td>
							<td class="bg-surface-3/60 px-3 py-2.5 align-middle transition-colors group-hover:bg-surface-3">
								<span class="text-sm text-surface-text-1">{{ promoValueLabel(promo) }}</span>
							</td>
							<td class="bg-surface-3/60 px-3 py-2.5 align-middle transition-colors group-hover:bg-surface-3">
								<span class="block max-w-[240px] truncate text-sm text-surface-text-1" :title="(promo.products || []).map((p) => p.title).join(', ')">{{ promoPlansLabel(promo) }}</span>
								<span v-if="promo.sync === 'partial'" class="block text-tiny text-warning" title="A plan's promo was removed on the Paywall side; delete and recreate the code to fix it.">Out of sync on the Paywall</span>
							</td>
							<td class="bg-surface-3/60 px-3 py-2.5 align-middle transition-colors group-hover:bg-surface-3">
								<button type="button" class="text-sm text-surface-text-1 underline decoration-surface-6 underline-offset-2 hover:text-white" :data-testid="`promo-${promo.id}-redemptions`" @click="openRedemptions(promo)">{{ redemptionsLabel(promo) }}</button>
							</td>
							<td class="bg-surface-3/60 px-3 py-2.5 align-middle transition-colors group-hover:bg-surface-3">
								<span class="text-nowrap text-sm text-surface-8">{{ fmtExpiry(promo) }}</span>
							</td>
							<td class="bg-surface-3/60 px-3 py-2.5 align-middle transition-colors group-hover:bg-surface-3">
								<StatusPill pill size="sm" :variant="promoStatus(promo).variant">{{ promoStatus(promo).label }}</StatusPill>
							</td>
							<td class="rounded-r-lg bg-surface-3/60 px-3 py-2.5 text-right align-middle transition-colors group-hover:bg-surface-3">
								<span class="inline-flex items-center gap-1">
									<FormButton type="secondary" size="sm" :disabled="busyId === promo.id" :aria-label="`${promo.active ? 'Deactivate' : 'Activate'} ${promo.code}`" @click="setActive(promo, !promo.active)">
										{{ promo.active ? 'Deactivate' : 'Activate' }}
									</FormButton>
									<FormButton is-icon type="danger" size="sm" :title="`Delete ${promo.code}`" :disabled="busyId === promo.id" @click="requestDelete(promo)">
										<icon-base :width="16" :height="16" fill="none"><icon-bin /></icon-base>
									</FormButton>
								</span>
							</td>
						</tr>
					</tbody>
				</table>
			</div>

			<div v-if="meta.total > PER_PAGE" class="mt-4 flex justify-center">
				<FormPagination v-model="page" :total-rows="meta.total" :per-page="PER_PAGE" />
			</div>
		</template>

		<!-- Create -->
		<ModalBox v-model="createOpen" title="New promo code" size="md" scroll-body wrapper-classes="lg:h-auto lg:max-h-[90vh]">
			<form class="flex flex-col gap-4 px-4 pb-4 sm:px-6 sm:pb-6" data-testid="promo-modal" @submit.prevent="submitCreate">
				<Alert v-if="createError" type="error" class="!mb-0" data-testid="promo-error">{{ createError }}</Alert>

				<div>
					<FormInput id="promo-code" :model-value="form.code" label="Code" placeholder="e.g. LAUNCH20" :maxlength="32" input-classes="font-mono uppercase placeholder:text-surface-6 placeholder:normal-case"
						:is-error="Boolean(codeErr)" @update:model-value="onCodeInput" />
					<p class="mb-0 mt-1.5 text-tiny" :class="codeErr ? 'text-error' : 'text-surface-7'">{{ codeErr || 'Letters, digits, “-” and “_”; 3–32 characters. Saved in upper case — viewers can type it either way.' }}</p>
				</div>

				<!-- Plans -->
				<section>
					<p class="mb-2 text-tiny font-medium text-surface-text-1">Applies to</p>
					<div class="flex flex-col gap-1.5 rounded-lg bg-surface-3 p-3">
						<FormCheckbox name="promo-all-plans" :input-value="true" size="md" :label="`All plans (${activePlans.length})`" :model-value="form.allPlans" @update:model-value="form.allPlans = $event" />
						<p class="mb-1 pl-5 text-tiny text-surface-7">Every active plan today. Plans you create later need their own code.</p>
						<div v-if="!form.allPlans" class="mt-1 flex max-h-48 flex-col gap-1 overflow-y-auto rounded bg-surface-2 p-2" data-testid="promo-plan-list">
							<FormCheckbox v-for="plan in activePlans" :key="plan.public_id" :name="`promo-plan-${plan.public_id}`" :input-value="true" size="md"
								:label="planLine(plan)" :model-value="form.productIds.includes(plan.public_id)" @update:model-value="togglePlan(plan.public_id, $event)" />
							<p v-if="!activePlans.length" class="mb-0 text-tiny text-surface-7">No active plans.</p>
						</div>
					</div>
					<p v-if="!selectedPlans.length" class="mb-0 mt-1 text-tiny text-error">Pick at least one plan.</p>
				</section>

				<!-- Type + value -->
				<section>
					<p class="mb-2 text-tiny font-medium text-surface-text-1">Discount</p>
					<TabGroup type="segmented" class="mb-2">
						<TabItem v-for="t in PROMO_TYPES" :key="t" :is-active="form.type === t" :disabled="typeDisabled(t)" :data-testid="`promo-type-${t}`" @click="!typeDisabled(t) && (form.type = t)">{{ PROMO_TYPE_LABELS[t] }}</TabItem>
					</TabGroup>
					<p v-if="recurringSelected.length" class="mb-2 rounded-md bg-surface-3 px-3 py-2 text-tiny text-surface-8" data-testid="promo-recurring-note">
						<template v-if="typeSwitched">Switched to Free: </template>{{ recurringSelected.length === 1 ? `“${recurringSelected[0].title}” is a subscription` : `${recurringSelected.length} of the selected plans are subscriptions` }} — the Paywall only accepts free (100 % off) codes on subscriptions. For a percentage or amount off, pick one-time plans only.
					</p>
					<p v-else class="mb-2 text-tiny text-surface-7" data-testid="promo-type-hint">Take a percentage or a fixed amount off the plan price, or make it free.</p>
					<div v-if="form.type !== 'comp'" class="flex items-end gap-2">
						<FormInput id="promo-value" v-model="form.value" type="number" :label="form.type === 'percent' ? 'Percent off' : `Amount off (${currencyLabel})`"
							:placeholder="form.type === 'percent' ? 'e.g. 20' : 'e.g. 5'" input-classes="placeholder:text-surface-6" :is-error="Boolean(valueErr) && form.value !== ''" class="!w-48" />
					</div>
					<p v-if="form.type !== 'comp' && valueErr && form.value !== ''" class="mb-0 mt-1 text-tiny text-error">{{ valueErr }}</p>
					<p v-if="previewLabel" class="mb-0 mt-1 text-tiny text-surface-7" data-testid="promo-preview">{{ previewLabel }}</p>
				</section>

				<!-- Limits -->
				<div class="grid gap-3 sm:grid-cols-2">
					<div>
						<FormInput id="promo-max" v-model="form.maxRedemptions" type="number" label="Max redemptions per plan (optional)" placeholder="Unlimited" :is-error="Boolean(maxErr)" />
						<p class="mb-0 mt-1.5 text-tiny" :class="maxErr ? 'text-error' : 'text-surface-7'">{{ maxErr || 'Counted per plan the code covers.' }}</p>
					</div>
					<div>
						<FormInput id="promo-expires" v-model="form.expiresAt" type="datetime-local" label="Expires (optional)" input-classes="[color-scheme:dark]" :is-error="Boolean(expiresErr)" />
						<p class="mb-0 mt-1.5 text-tiny" :class="expiresErr ? 'text-error' : 'text-surface-7'">{{ expiresErr || 'Leave empty for no expiry.' }}</p>
					</div>
				</div>
			</form>
			<template #footer>
				<footer class="flex flex-col gap-2 border-t border-surface-3 p-4 sm:flex-row-reverse sm:p-6">
					<FormButton class="w-full justify-center sm:w-auto" :disabled="!canCreate" :is-loading="creating" @click="submitCreate">Create code</FormButton>
					<FormButton class="w-full justify-center sm:w-auto" type="secondary" @click="createOpen = false">Cancel</FormButton>
				</footer>
			</template>
		</ModalBox>

		<!-- Redemptions -->
		<ModalBox v-model="drawerOpen" :title="drawerPromo ? `Redemptions · ${drawerPromo.code}` : 'Redemptions'" placement="right" size="md" scroll-body>
			<div class="px-4 pb-4 sm:px-6 sm:pb-6" data-testid="redemptions-drawer">
				<p v-if="drawerPromo" class="mb-3 text-tiny text-surface-7">
					{{ promoValueLabel(drawerPromo) }} · {{ promoPlansLabel(drawerPromo) }} · {{ redemptionsLabel(drawerPromo) }}
				</p>
				<Spinner v-if="redemptionsLoading" text="Loading…" classes="text-surface-8 my-12" spinner-color="var(--c-dark-8)" spinner-size="15px" />
				<Alert v-else-if="redemptionsError" type="error" class="!mb-0">{{ describeError(redemptionsError, 'Could not load the redemptions.') }}</Alert>
				<p v-else-if="!redemptions.length" class="mb-0 rounded-lg bg-surface-3 px-3 py-2.5 text-tiny text-surface-7">Nobody has used this code yet.</p>
				<ul v-else class="m-0 flex list-none flex-col gap-1.5 p-0">
					<li v-for="(r, i) in redemptions" :key="`${r.invoice || i}-${r.promo_id}`" class="flex items-center gap-3 rounded-lg bg-surface-3 px-3 py-2 text-sm" :data-testid="`redemption-${r.invoice || i}`">
						<span class="min-w-0 flex-1">
							<span class="block truncate text-white">{{ r.viewer?.name || r.viewer?.email || (r.viewer?.id ? `Viewer #${r.viewer.id}` : '—') }}</span>
							<span class="block truncate text-tiny text-surface-7">
								<template v-if="r.viewer?.name && r.viewer?.email">{{ r.viewer.email }} · </template>{{ r.product?.title || 'Removed plan' }} · {{ fmtDateTime(r.redeemed_at) }}
							</span>
						</span>
						<span class="shrink-0 text-right">
							<span class="block text-sm text-surface-text-1">{{ r.free ? 'Free' : formatMoney(r.amount_paid, r.currency) }}</span>
							<span v-if="r.discount_amount !== null && r.discount_amount !== undefined" class="block text-tiny text-surface-7">−{{ formatMoney(r.discount_amount, r.currency) }}</span>
						</span>
						<StatusPill pill size="sm" :variant="TX_STATUS_VARIANTS[r.status] || 'muted'">{{ TX_STATUS_LABELS[r.status] || r.status }}</StatusPill>
					</li>
				</ul>
				<div v-if="redemptionsMeta.total > PER_PAGE" class="mt-4 flex justify-center">
					<FormPagination v-model="redemptionsPage" :total-rows="redemptionsMeta.total" :per-page="PER_PAGE" />
				</div>
			</div>
		</ModalBox>

		<ConfirmModal modal-id="delete-site-promo" modal-type="danger" ok-text="Delete code" cancel-text="Cancel"
			title="Delete this promo code?" :message="`${pendingDelete?.code || 'The code'} stops working immediately on every plan it covers. Purchases already made with it are not affected. To pause it instead, use Deactivate.`"
			@modal-confirm="confirmDelete" />
	</div>
</template>
